import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App'
import { initSentry, Sentry } from './lib/sentry'
import { initAnalytics } from './lib/analytics'

initSentry()
// GA4 wiring. No-op when VITE_GA_MEASUREMENT_ID isn't set, so this
// is safe to call unconditionally — the gtag.js loader only injects
// when the env var is populated.
initAnalytics()

// Self-heal stale chunk references after a redeploy. When Vite produces new
// asset hashes, a tab that's still running the previous build will try to
// dynamic-import chunk URLs that no longer exist on Cloudflare Pages — Pages
// then serves the SPA fallback (text/html), and Chrome reports
// "'text/html' is not a valid JavaScript MIME type." We catch that pattern
// and force a one-shot reload so the user picks up the fresh index.html.
function shouldHardReloadFor(message?: string): boolean {
  if (!message) return false
  return (
    message.includes('is not a valid JavaScript MIME type') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed')
  )
}
function attemptChunkRecoveryReload() {
  // Guard against reload loops if the new build is also broken.
  const key = 'drinx-admin-chunk-recovery-at'
  const last = Number(sessionStorage.getItem(key) || '0')
  if (Date.now() - last < 60_000) return
  sessionStorage.setItem(key, String(Date.now()))
  window.location.reload()
}
window.addEventListener('error', e => {
  if (shouldHardReloadFor(e.message)) attemptChunkRecoveryReload()
})
window.addEventListener('unhandledrejection', e => {
  const msg = e.reason instanceof Error ? e.reason.message : String(e.reason ?? '')
  if (shouldHardReloadFor(msg)) attemptChunkRecoveryReload()
})

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<FatalError />} showDialog>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
)

function FatalError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] text-white p-8">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold mb-2">Something went wrong.</h1>
        <p className="text-gray-400 text-sm mb-6">
          The error has been reported to our team. Try reloading the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold"
        >
          Reload
        </button>
      </div>
    </div>
  )
}
